import { useCallback, useMemo, useRef, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client/react'
import chunk from 'lodash/chunk'
import { getWebClient } from '@/services/ApolloClient/ApolloClient'
import {
  CreateMultipartUploadMutation,
  GetFilmmakerMultipartUploadUrlQuery,
  GetFilmmakerUploadUrlQuery,
  GetMultipartUploadUrlQuery,
  GetUploadUrlQuery,
} from '@/types/codegen-federation'
import { logger } from '@/utils/logging'
import {
  ABORT_MULTIPART_UPLOAD,
  COMPLETE_MULTIPART_UPLOAD,
  COMPLETE_UPLOAD,
  CREATE_MULTIPART_UPLOAD,
  GET_FILMMAKER_MULTIPART_UPLOAD_URL,
  GET_FILMMAKER_UPLOAD_URL,
  GET_MULTIPART_UPLOAD_URL,
  GET_UPLOAD_URL,
} from '../queries'

const CHUNK_BYTE_SIZE = 10_485_760 // 10MB
const CONCURRENT_UPLOADS = 10

const getSmallUploadQueryAndVariables = (
  id: string,
  type: 'APPLICATION' | 'UPDATE' | 'PROJECT',
  fileName?: string,
  assetId?: string,
  projectApplicationId?: string,
  mime?: string,
) => {
  const query = type === 'APPLICATION' ? GET_UPLOAD_URL : GET_FILMMAKER_UPLOAD_URL
  const variables =
    type === 'APPLICATION'
      ? { id, type, fileName }
      : { input: { id, type, fileName, assetId, projectApplicationId, mime } }

  return {
    query,
    variables,
  }
}

const getLargeUploadQueryAndVariables = ({
  uploadId,
  id,
  type,
  fileName,
  part,
  assetId,
  mime,
}: {
  uploadId: string
  id: string
  type: 'APPLICATION' | 'UPDATE' | 'PROJECT'
  fileName?: string
  part?: number
  assetId?: string
  mime?: string
}) => {
  const query = type === 'APPLICATION' ? GET_MULTIPART_UPLOAD_URL : GET_FILMMAKER_MULTIPART_UPLOAD_URL
  const variables =
    type === 'APPLICATION'
      ? { uploadId, id, fileName, part, type }
      : { input: { uploadId, id, fileName, part, type, assetId, mime } }
  return { query, variables }
}

interface Props {
  id: string
  type: 'APPLICATION' | 'UPDATE' | 'PROJECT'
  fileName?: string
  assetId?: string
  projectApplicationId?: string
  mime?: string
}

export const useMultipartUpload = ({
  id,
  type,
  fileName: optionalFileName,
  assetId,
  projectApplicationId,
  mime,
}: Props): [(file: File, mimeOverride?: string) => Promise<void>, () => void, number, boolean] => {
  const [partsProgress, setPartsProgress] = useState<number[]>([])
  const [isPreparingUpload, setIsPreparingUpload] = useState(false)
  const xhrQueueRef = useRef<XMLHttpRequest[]>([])
  const uploadIdRef = useRef<string | null>(null)
  const fileRef = useRef<File | null>(null)
  const abortedRef = useRef<boolean>(false)

  const apolloClient = useApolloClient()
  const localClient = getWebClient({ baseUrl: process.env.NEXT_PUBLIC_LOCAL_GRAPHQL as string })
  const client = process.env.NEXT_PUBLIC_LOCAL_GRAPHQL ? localClient : apolloClient

  const [createMultipartUpload] = useMutation<CreateMultipartUploadMutation>(CREATE_MULTIPART_UPLOAD, {
    client,
    errorPolicy: 'all',
  })
  const [completeMultipartUpload] = useMutation(COMPLETE_MULTIPART_UPLOAD, {
    errorPolicy: 'all',
    client,
  })
  const [completeUpload] = useMutation(COMPLETE_UPLOAD, {
    errorPolicy: 'all',
    client,
  })
  const [abortMultipartUpload] = useMutation(ABORT_MULTIPART_UPLOAD, {
    errorPolicy: 'all',
    client,
  })

  const uploadXhr = useCallback((url?: string, part = 1) => {
    if (!url) {
      return Promise.reject(new Error('No upload url'))
    }

    return new Promise<{ part: number; eTag: string }>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhrQueueRef.current.push(xhr)

      function removeXhr() {
        xhrQueueRef.current = xhrQueueRef.current.filter((xhr) => xhr !== xhr)
      }

      xhr.open('PUT', url)
      xhr.setRequestHeader('Content-Type', 'octet/stream')
      xhr.upload.onprogress = (event) => {
        setPartsProgress((current) => {
          const next = [...current]
          next[part - 1] = event.total === 0 ? 0 : Math.round((event.loaded / event.total) * 100)
          return next
        })
      }

      xhr.onload = () => {
        removeXhr()
        if (abortedRef.current) return
        if (xhr.status === 200) {
          const eTag = xhr.getResponseHeader('ETag')?.replace(/"/g, '') ?? ''
          resolve({ part, eTag })
        } else if (xhr.status !== 404) {
          reject(new Error(`Failed to upload file: ${xhr.statusText}`))
        }
      }

      const partIndex = part - 1
      const start = partIndex * CHUNK_BYTE_SIZE

      if (!fileRef.current) {
        removeXhr()
        return
      }

      const end = Math.min(start + CHUNK_BYTE_SIZE, fileRef.current.size)
      const blob = fileRef.current.slice(start, end)

      xhr.send(blob)
      xhr.onerror = () => {
        removeXhr()
        reject(new Error(`Failed to upload file: ${xhr.statusText}`))
      }
      xhr.onabort = () => {
        removeXhr()
      }
    })
  }, [])

  const handleSmallFileRequest = useCallback(
    async (fileName: string, mimeOverride?: string) => {
      setIsPreparingUpload(true)
      const { query, variables } = getSmallUploadQueryAndVariables(
        id,
        type,
        fileName,
        assetId,
        projectApplicationId,
        mimeOverride ?? mime,
      )

      const res = await client?.query<GetUploadUrlQuery | GetFilmmakerUploadUrlQuery>({
        query,
        variables,
        errorPolicy: 'all',
      })
      const resData = res?.data
      const url = resData
        ? ('torchApplication' in resData ? resData.torchApplication?.uploadUrl : undefined) ||
          ('filmmakerUploadUrl' in resData ? resData.filmmakerUploadUrl : undefined)
        : undefined
      setIsPreparingUpload(false)
      const uploadRes = await uploadXhr(url ?? undefined)
      if (uploadRes && type !== 'APPLICATION') {
        if (abortedRef.current) return
        setPartsProgress([100])
        await completeUpload({
          variables: {
            input: {
              id,
              type,
              fileName,
              assetId,
              mime: mimeOverride ?? mime,
            },
          },
        })
      }
    },
    [id, type, assetId, projectApplicationId, mime, client, uploadXhr, completeUpload, setPartsProgress],
  )

  const uploadMultipart = useCallback(
    (file: File, mimeOverride?: string) => {
      if (xhrQueueRef.current.length) {
        return Promise.reject(new Error('Upload already in progress'))
      }

      abortedRef.current = false
      fileRef.current = file
      const fileName = `${optionalFileName ? `<<|${optionalFileName}|>>` : ''}${file.name}`

      if (file.size < CHUNK_BYTE_SIZE) {
        return handleSmallFileRequest(fileName, mimeOverride)
      }

      const parts = Math.ceil(file.size / CHUNK_BYTE_SIZE)
      setPartsProgress(new Array(parts).fill(0))

      const uploadedParts: { part: number; eTag: string | null }[] = Array(parts)
        .fill(0)
        .map((_, i) => ({ part: i + 1, eTag: null }))

      setIsPreparingUpload(true)
      return new Promise<void>((resolve, reject) => {
        return createMultipartUpload({
          variables: { input: { fileName, id, type, assetId } },
          async onCompleted(data) {
            const uploadId = data?.createFilmmakerMultipartUpload?.uploadId
            uploadIdRef.current = uploadId ?? null

            if (!uploadId) {
              setIsPreparingUpload(false)
              reject(new Error('Failed to create multipart upload'))
              return
            }
            setIsPreparingUpload(false)

            for await (const partsChunk of chunk(uploadedParts, CONCURRENT_UPLOADS)) {
              await Promise.all(
                partsChunk.map(({ part }) => {
                  const { query, variables } = getLargeUploadQueryAndVariables({
                    uploadId,
                    id,
                    type,
                    fileName,
                    part,
                    assetId,
                    mime,
                  })
                  return client
                    ?.query<GetMultipartUploadUrlQuery | GetFilmmakerMultipartUploadUrlQuery>({
                      query,
                      variables,
                      fetchPolicy: 'network-only',
                      errorPolicy: 'all',
                    })
                    .then((res) => {
                      const resData = res?.data
                      const url = resData
                        ? ('torchApplication' in resData
                            ? (resData.torchApplication?.uploadMultipartUrl as string)
                            : undefined) ||
                          ('filmmakerUploadMultipartUrl' in resData
                            ? (resData.filmmakerUploadMultipartUrl as string)
                            : undefined)
                        : undefined
                      return uploadXhr(url, part)
                    })
                    .then((data) => {
                      const uploadedPart = uploadedParts[data.part - 1]

                      if (uploadedPart) {
                        uploadedPart.eTag = data.eTag
                      }
                    })
                }),
              )
            }

            if (abortedRef.current) return

            completeMultipartUpload({
              variables: {
                input: {
                  uploadId,
                  id,
                  fileName,
                  parts: uploadedParts,
                  type,
                  assetId,
                },
              },
              onCompleted() {
                setPartsProgress([100])
                resolve()
              },
            }).catch((error) => {
              logger().error('Failed to complete multipart upload', { error, uploadId, id, fileName, type, assetId })
              abortMultipartUpload({ variables: { input: { uploadId, id, fileName, type, assetId } } })
              xhrQueueRef.current.forEach((xhr) => xhr.abort())
              setPartsProgress([0])
              reject(error)
            })
          },
        })
      })
    },
    [
      optionalFileName,
      id,
      type,
      assetId,
      mime,
      client,
      uploadXhr,
      createMultipartUpload,
      completeMultipartUpload,
      abortMultipartUpload,
      handleSmallFileRequest,
    ],
  )

  const abort = useCallback(() => {
    abortedRef.current = true

    xhrQueueRef.current.forEach((xhr) => xhr.abort())
    xhrQueueRef.current = []
    setPartsProgress([0])
    setIsPreparingUpload(false)

    if (uploadIdRef.current) {
      abortMultipartUpload({
        variables: {
          input: {
            uploadId: uploadIdRef.current,
            id,
            fileName: fileRef.current?.name,
            type,
            mime,
            assetId,
          },
        },
      }).catch((error) => {
        logger().error('Failed to abort multipart upload', {
          error,
          uploadId: uploadIdRef.current,
          id,
          fileName: fileRef.current?.name,
          type,
          assetId,
        })
      })
      uploadIdRef.current = null
    }
    fileRef.current = null
  }, [abortMultipartUpload, id, type, mime, assetId])

  const uploadProgress = useMemo(() => {
    const totalProgress = partsProgress.reduce((a, b) => a + (b || 0), 0) || 0

    if (totalProgress === 0 || !partsProgress.length) {
      return 0
    }

    return Math.floor(Math.round((totalProgress / partsProgress.length) * 100) / 100)
  }, [partsProgress])

  return [uploadMultipart, abort, uploadProgress, isPreparingUpload]
}
